import React, { useEffect, useMemo, useState } from "react";
import { RxCross2 } from "react-icons/rx";
import { User } from "src/entities/user.entity";
import { getCookieWithKey } from "src/utils/browserStorage.util";
import { showToast } from "src/utils/toast.util";
import { AICoachPrompt } from "../entities/ai-coach-prompt.entity";
import { useRequestAICoachPromptReplacementMutation } from "../ai-coach-prompts.service";

type Props = {
  isOpen: boolean;
  prompt: (AICoachPrompt & { id: string }) | null;
  onClose: () => void;
};

export const RequestPromptReplacementModal: React.FC<Props> = ({ isOpen, prompt, onClose }) => {
  const [requestPromptReplacement, { isLoading }] = useRequestAICoachPromptReplacementMutation();
  const [requestedPrompt, setRequestedPrompt] = useState("");
  const [confirmOverwrite, setConfirmOverwrite] = useState(false);

  const currentAdmin = useMemo(() => {
    try {
      return getCookieWithKey("current-admin") as User;
    } catch (error) {
      return undefined;
    }
  }, []);

  const hasPendingRequest = prompt?.requestStatus === "PENDING" && Boolean(prompt?.requestedPrompt?.trim());

  useEffect(() => {
    if (!prompt) return;

    setRequestedPrompt(prompt.requestedPrompt?.trim() || prompt.currentPrompt || "");
    setConfirmOverwrite(false);
  }, [prompt]);

  if (!isOpen || !prompt) return null;

  const handleSubmit = async (event: React.FormEvent<HTMLFormElement>) => {
    event.preventDefault();

    const cleanedPrompt = requestedPrompt.trim();
    if (!cleanedPrompt) {
      showToast("warrning", "Please write the new prompt content before submitting.");
      return;
    }

    if (hasPendingRequest && !confirmOverwrite) {
      showToast("warrning", "Please confirm that you want to replace the current pending request.");
      return;
    }

    try {
      await requestPromptReplacement({
        promptKey: prompt.promptKey,
        requestedPrompt: cleanedPrompt,
        requestedAt: new Date().toISOString(),
        requestedByAdminId: currentAdmin?.id,
        requestedByAdminName: currentAdmin?.name,
      }).unwrap();

      showToast("success", "Prompt replacement request submitted successfully.");
      onClose();
    } catch (error) {
      showToast("error", (error as { error?: string })?.error || "Failed to submit prompt replacement request.");
    }
  };

  return (
    <div className="fixed inset-0 z-50 flex items-center justify-center bg-slate-950/70 p-4 backdrop-blur-sm">
      <div className="relative max-h-[92vh] w-full max-w-4xl overflow-hidden rounded-[28px] border border-slate-200 bg-slate-50 shadow-2xl">
        <div className="flex items-start justify-between gap-6 border-b border-slate-200 bg-white px-6 py-5">
          <div>
            <p className="text-xs font-semibold uppercase tracking-[0.24em] text-cyan-700">Request replacement</p>
            <h2 className="mt-1 text-2xl font-semibold text-slate-900">Request Prompt Replacement</h2>
            <p className="mt-2 text-sm text-slate-600">
              Submit the new prompt content for <span className="font-semibold text-slate-900">{prompt.promptKey}</span>.
            </p>
          </div>
          <button
            type="button"
            onClick={onClose}
            className="rounded-full border border-slate-200 bg-white p-2 text-slate-500 transition-colors hover:border-slate-300 hover:text-slate-700"
            aria-label="Close modal"
          >
            <RxCross2 className="text-xl" />
          </button>
        </div>

        <form onSubmit={handleSubmit} className="max-h-[calc(92vh-92px)] overflow-y-auto px-6 py-6">
          <div className="rounded-[28px] border border-slate-200 bg-white shadow-sm">
            <div className="border-b border-slate-200 px-5 py-4">
              <p className="text-[11px] font-semibold uppercase tracking-[0.2em] text-slate-500">Prompt ID</p>
              <h3 className="mt-1 break-all font-mono text-sm font-semibold text-slate-950">{prompt.promptKey}</h3>
            </div>

            <div className="space-y-4 p-5">
              {hasPendingRequest ? (
                <label className="flex items-start gap-3 rounded-2xl border border-amber-200 bg-amber-50 px-4 py-4 text-sm text-amber-900">
                  <input
                    type="checkbox"
                    checked={confirmOverwrite}
                    onChange={(event) => setConfirmOverwrite(event.target.checked)}
                    className="mt-1 h-4 w-4 rounded border-amber-300 text-amber-600 focus:ring-amber-400"
                  />
                  <span>
                    Replace the current pending request for <span className="font-semibold">{prompt.promptKey}</span>.
                  </span>
                </label>
              ) : null}

              <div>
                <label htmlFor="requestedPrompt" className="mb-2 block text-sm font-semibold text-slate-800">
                  New requested prompt
                </label>
                <textarea
                  id="requestedPrompt"
                  value={requestedPrompt}
                  onChange={(event) => setRequestedPrompt(event.target.value)}
                  className="min-h-[520px] w-full rounded-2xl border border-slate-300 bg-white px-4 py-4 font-mono text-[12px] leading-6 text-slate-900 shadow-sm transition-all focus:border-cyan-500 focus:outline-none focus:ring-2 focus:ring-cyan-200"
                  placeholder="Paste or write the new prompt here..."
                />
                <div className="mt-2 text-right text-xs text-slate-500">
                  {requestedPrompt.length.toLocaleString()} characters
                </div>
              </div>

              <div className="flex flex-col gap-3 border-t border-slate-200 pt-4 sm:flex-row sm:items-center sm:justify-end">
                <button
                  type="button"
                  onClick={onClose}
                  className="rounded-full border border-slate-300 bg-white px-5 py-2.5 text-sm font-semibold text-slate-700 transition-colors hover:border-slate-400 hover:bg-slate-100"
                >
                  Cancel
                </button>
                <button
                  type="submit"
                  disabled={isLoading}
                  className="rounded-full bg-slate-950 px-5 py-2.5 text-sm font-semibold text-white shadow-lg shadow-slate-950/15 transition-all hover:bg-slate-800 disabled:cursor-not-allowed disabled:opacity-60"
                >
                  {isLoading ? "Submitting..." : hasPendingRequest ? "Replace request" : "Submit request"}
                </button>
              </div>
            </div>
          </div>
        </form>
      </div>
    </div>
  );
};
